import React, { useEffect, useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
} from "@mui/material";
import dayjs from "dayjs";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import {
  SelectInputComponent,
  TextInputComponent,
} from "../../../../../common/components";
import { formatObjectivePayload } from "../../../company-dashboard-utils";
import {
  addObjective,
  updateObjective,
  getObjectivesById,
} from "../../../../../api/objectives";
import DatePickerComponent from "../../../../../common/components/date-picker/date-picker-compoenent";
import CloseIcon from "../../../../../assets/icons/closeIcon.svg";

export interface StatusOption {
  statusId: number;
  statusName: string;
}

const CompanyDashboardAddObjectivePopup: any = (props: any) => {
  const {
    isOpenAddPopup,
    onCancel,
    newObjectiveData,
    setNewObjectiveData,
    employeesList,
    statusListObjectives,
    setAlertType,
    setAlertOpen,
    setLoading,
    setAlertMessage,
    setObjectiveId,
    objectiveId,
    updateInitiativeById,
  } = props;
  const [objectiveResponse, setObjectiveResponse] = useState<any>(null);

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    owner: Yup.mixed().required("Owner is required"),
  });

  const handleCreateObjective = async (newObjectiveData: any) => {
    setLoading(true);
    try {
      const payload = formatObjectivePayload(newObjectiveData, objectiveId);
      const response = objectiveId
        ? await updateObjective(objectiveId, payload)
        : await addObjective(payload);
      if (objectiveId) {
        updateInitiativeById();
        setAlertMessage("Objective updated successfully");
      } else {
        setObjectiveId(response?.data?.data?.[0]?.pk);
        setAlertMessage("Objective created successfully");
        setNewObjectiveData({
          objectiveName: response?.data?.data?.[0]?.objectiveName,
          startDate: response?.data?.data?.[0]?.startDate,
          weightage: 0,
        });
      }
      setAlertType("success");
      setAlertOpen(true);
      onCancel();
      setLoading(false);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to create objective",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const fetchObjectiveById = async (objectiveUpdateId: any) => {
    setLoading(true);
    try {
      const response = await getObjectivesById({ objectId: objectiveUpdateId });
      setObjectiveResponse(response?.data?.data);
      setTimeout(() => setLoading(false), 0);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch objective",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (objectiveId) {
      fetchObjectiveById(objectiveId);
    }
  }, [objectiveId]);

  return (
    <Dialog
      open={isOpenAddPopup}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={`company-dashboard-add-popup-container `}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onCancel();
      }}
    >
      <DialogTitle id="alert-dialog-title">
        <div className="header-wrapper">
          <div className="header-wrapper-text">
            {objectiveId ? "Edit Objective" : "Add Objective"}{" "}
          </div>
          <div className="image-wrapper">
            <button
              style={{
                border: "none",
                background: "none",
                padding: 0,
              }}
              onClick={onCancel}
            >
              <img src={CloseIcon} className="close-icon" alt="close" />
            </button>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            owner:
              objectiveResponse?.owners?.map(
                (emp: any) =>
                  employeesList?.find(
                    (e: any) => e.employeeId === emp.employeeId,
                  )?.employeeFullName,
              ) || [],
            ownerId:
              objectiveResponse?.owners?.map(
                (emp: any) =>
                  employeesList?.find(
                    (owner: any) =>
                      emp.employeeFullName === owner.employeeFullName,
                  )?.employeeId || null,
              ) || [],

            title: objectiveResponse?.objectiveName || "",
            status: objectiveResponse?.status
              ? statusListObjectives.find(
                  (s: any) => s.statusName === objectiveResponse?.status,
                ) || null
              : statusListObjectives.find(
                  (s: any) => s.statusName === "Not Started",
                ) || null,
            startDate: objectiveResponse?.startDate
              ? dayjs(objectiveResponse?.startDate)
              : dayjs(),
            endDate: objectiveResponse?.endDate
              ? dayjs(objectiveResponse?.endDate)
              : dayjs(),
          }}
          validationSchema={validationSchema}
          validateOnChange={true}
          enableReinitialize={true}
          onSubmit={(values, { resetForm }) => {
            const originalOwnerIds =
              newObjectiveData?.owners?.map((o: any) => o.employeeId) || [];

            const currentOwnerIds = values.ownerId || [];

            const ownerIds = [
              ...originalOwnerIds
                .filter((id: number) => !currentOwnerIds.includes(id))
                .map((id: number) => ({ ownerId: id, isRemove: 1 })),
              ...currentOwnerIds.map((id: number) => ({
                ownerId: id,
                isRemove: 0,
              })),
            ];
            const newObjective = {
              status: values.status.statusName,
              endDate: values.endDate,
              startDate: values.startDate,
              objectiveName: values.title,
              ownerIds,
              empty: true,
            };

            handleCreateObjective(newObjective);
            onCancel();
            resetForm();
          }}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="create-lead-capture-popup-confirmation-description-checkbox-input">
                <div className="form-group">
                  <TextInputComponent
                    type="text"
                    fieldName="title"
                    label="Title"
                    placeholder="Enter Title"
                    disabled={false}
                    value={values.title}
                    onChange={(e: any) =>
                      setFieldValue("title", e?.target?.value)
                    }
                    maxlength="150"
                  />
                </div>
                <div className="form-group">
                  <SelectInputComponent
                    fieldName="owner"
                    placeholder="Select Owner"
                    label="Owner"
                    optional={false}
                    disabled={false}
                    multiple={true}
                    isOwnerDropdown={true}
                    options={employeesList
                      ?.filter((emp: any) =>
                        emp?.roles?.find(
                          (role: any) => role?.roleName === "ELT",
                        ),
                      )
                      ?.map((emp: any) => {
                        return {
                          value: emp.employeeFullName,
                          label: emp.employeeFullName,
                          image: emp.employeeImage || null,
                          avatar: emp.employeeImage
                            ? null
                            : emp.employeeFullName,
                        };
                      })}
                    value={values.owner}
                    onChange={(e: any) => {
                      setFieldValue("owner", e);
                      setFieldValue(
                        "ownerId",
                        e?.map(
                          (eValue: any) =>
                            employeesList?.find(
                              (emp: any) =>// NOSONAR
                               emp.employeeFullName === eValue,
                            )?.employeeId,
                        ),
                      );
                    }}
                  />
                  <ErrorMessage
                    name={"owner"}
                    component="div"
                    className="error-message"
                  />
                </div>
                <div className="form-group">
                  <DatePickerComponent
                    fieldName={"startDate"}
                    label="Created Date "
                    value={values?.startDate}
                    disabled={true}
                    onChange={(e: any) => {
                      setFieldValue("startDate", e);
                    }}
                  />
                </div>
                <div className="form-group form-group-status form-text-input-component">
                  <InputLabel>Status</InputLabel>

                  <Autocomplete<StatusOption>
                    value={values.status}
                    onChange={(event, newValue) => {
                      setFieldValue("status", newValue);
                    }}
                    options={statusListObjectives}
                    getOptionLabel={(option) => option.statusName}
                    isOptionEqualToValue={(option, value) =>
                      option.statusId === value.statusId
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Status" />
                    )}
                  />
                </div>
              </div>
              <DialogActions>
                <div className="action-btn-wrapper">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                  <button type="submit" autoFocus className="action-btn">
                    Add
                  </button>
                </div>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyDashboardAddObjectivePopup;
